import {
  mapAdminBindResultDto,
  mapBulkReassignResultDto,
  mapRecomputePeriodResultDto,
  mapReferralAuditLogsDto,
  mapReferralBindingDto,
  mapReferralCodeDto,
  mapReferralCodeListDto,
  mapReferralCurrentBindingDto,
  mapReferralCustomersDto,
  mapReferralPeriodSummaryDto,
  mapReferralStatementDetailDto,
  mapReferralStatementDto,
  mapRepDetailDto,
  mapSalesRepDto,
  mapSalesRepListDto,
  mapSelfBindResultDto,
  type AdminBindResultDto,
  type BulkReassignResultDto,
  type CodeUpdateResultDto,
  type RecomputePeriodResultDto,
  type ReferralAuditLogsDto,
  type ReferralBindingHistoryDto,
  type ReferralCodeDto,
  type ReferralCodeListDto,
  type ReferralCurrentBindingDto,
  type ReferralCustomersDto,
  type ReferralPeriodSummaryDto,
  type ReferralStatementDetailDto,
  type ReferralStatementDto,
  type RepDetailDto,
  type SalesRepDto,
  type SalesRepListDto,
  type SelfBindResultDto,
} from '@/types/referral';
import { ApiHttpError } from '@/types';

import type { ReferralService } from '../referralService';
import {
  buildApiUrl,
  extractDetailObjectError,
  httpRequestWithRefresh,
  normalizeEnvelopeError,
  type HttpClientConfig,
} from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`ReferralService`(R392;契約 = BE docs/128 / MR !121)
 * -----------------------------------------------------------------------------
 * - request 全部 `extra="forbid"`:**選填欄 undefined 不上 wire**(缺席才是「交給
 *   BE 預設」);A4/A7 的 PATCH 是 `model_fields_set` 語意 —— **顯式 `null` 要原樣
 *   上 wire**(解綁 / 清覆寫),所以 body 組裝以「鍵在 patch 物件上出現且非
 *   undefined」為準,不可無條件塞 key。
 * - A11 是 DELETE 帶 body(BE 先例:ads accounts bulk unlink);transport 對非
 *   GET/HEAD 自動帶 CSRF。
 * - A21 CSV 是 **raw 回應不是 envelope** → 獨立 `fetch`(credentials: 'include',
 *   同源 cookie;GET 免 CSRF),失敗時仍解析 envelope error 丟 `ApiHttpError`。
 *   ⚠️ 這條刻意繞過 httpRequest 的 401-refresh 鏈:匯出必然發生在 admin 已載入
 *   清單之後(session 熱),401 極端情況下錯誤會誠實浮出,重按即可。
 */

const BASE = '/api/admin/referral';

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** 選填欄「有值才上 wire」(undefined / 空字串 = 缺席,交 BE 預設)。 */
function putIf(body: Record<string, unknown>, key: string, value: unknown): void {
  if (value === undefined || value === '') return;
  body[key] = value;
}

export function createHttpReferralService(config: HttpClientConfig): ReferralService {
  return {
    // ── 客戶面(C1/C2;`/api/referral/**`,X-Current-Group 由 transport 注入)────
    async getCurrentBinding() {
      const env = await httpRequestWithRefresh<ReferralCurrentBindingDto>(
        config,
        'GET',
        '/api/referral/current',
      );
      return mapReferralCurrentBindingDto(unwrapResult(env, 'referral.getCurrentBinding'));
    },

    async bindCode(code) {
      // body 只有 `code` 一欄(BE `extra="forbid"`);大小寫/dash 原樣送,正規化在 BE。
      const env = await httpRequestWithRefresh<SelfBindResultDto>(
        config,
        'POST',
        '/api/referral/bind',
        { code },
      );
      return mapSelfBindResultDto(unwrapResult(env, 'referral.bindCode'));
    },

    // ── 業務(A1–A4)──────────────────────────────────────────────────────────
    async createRep(req) {
      const body: Record<string, unknown> = {
        name: req.name,
        default_commission_bps: req.defaultCommissionBps,
      };
      putIf(body, 'contact_email', req.contactEmail);
      putIf(body, 'note', req.note);
      const env = await httpRequestWithRefresh<SalesRepDto>(config, 'POST', `${BASE}/reps`, body);
      return mapSalesRepDto(unwrapResult(env, 'referralAdmin.createRep'));
    },

    async listReps(opts) {
      const env = await httpRequestWithRefresh<SalesRepListDto>(
        config,
        'GET',
        `${BASE}/reps${qs({ status: opts?.status, q: opts?.q, limit: opts?.limit, cursor: opts?.cursor })}`,
      );
      return mapSalesRepListDto(unwrapResult(env, 'referralAdmin.listReps'));
    },

    async getRep(repId) {
      const env = await httpRequestWithRefresh<RepDetailDto>(
        config,
        'GET',
        `${BASE}/reps/${encodeURIComponent(repId)}`,
      );
      return mapRepDetailDto(unwrapResult(env, 'referralAdmin.getRep'));
    },

    async updateRep(repId, patch) {
      // A4 是 fields_set 語意:只送呼叫端真的放進 patch 的鍵;`linkedCuid: null`
      // (解綁)要原樣上 wire —— 所以不能用 putIf(它會把 null 當缺席丟掉)。
      const body: Record<string, unknown> = {};
      if (patch.name !== undefined) body.name = patch.name;
      if (patch.status !== undefined) body.status = patch.status;
      if (patch.defaultCommissionBps !== undefined)
        body.default_commission_bps = patch.defaultCommissionBps;
      if ('linkedCuid' in patch && patch.linkedCuid !== undefined) body.linked_cuid = patch.linkedCuid;
      if ('contactEmail' in patch && patch.contactEmail !== undefined)
        body.contact_email = patch.contactEmail;
      if (patch.note !== undefined) body.note = patch.note;
      const env = await httpRequestWithRefresh<SalesRepDto>(
        config,
        'PATCH',
        `${BASE}/reps/${encodeURIComponent(repId)}`,
        body,
      );
      return mapSalesRepDto(unwrapResult(env, 'referralAdmin.updateRep'));
    },

    // ── 推薦碼(A5–A7)────────────────────────────────────────────────────────
    async createCode(req) {
      const body: Record<string, unknown> = { mode: req.mode, rep_id: req.repId };
      putIf(body, 'code', req.code);
      putIf(body, 'label', req.label);
      putIf(body, 'commission_bps_override', req.commissionBpsOverride);
      putIf(body, 'max_bindings', req.maxBindings);
      const env = await httpRequestWithRefresh<ReferralCodeDto>(
        config,
        'POST',
        `${BASE}/codes`,
        body,
      );
      return mapReferralCodeDto(unwrapResult(env, 'referralAdmin.createCode'));
    },

    async listCodes(opts) {
      const env = await httpRequestWithRefresh<ReferralCodeListDto>(
        config,
        'GET',
        `${BASE}/codes${qs({ rep_id: opts?.repId, status: opts?.status, q: opts?.q, limit: opts?.limit, cursor: opts?.cursor })}`,
      );
      return mapReferralCodeListDto(unwrapResult(env, 'referralAdmin.listCodes'));
    },

    async updateCode(code, patch) {
      // A7 同 A4 的 fields_set 語意;`commissionBpsOverride: null` = 清除覆寫。
      const body: Record<string, unknown> = {};
      if (patch.label !== undefined) body.label = patch.label;
      if (patch.status !== undefined) body.status = patch.status;
      if ('commissionBpsOverride' in patch && patch.commissionBpsOverride !== undefined)
        body.commission_bps_override = patch.commissionBpsOverride;
      if (patch.repId !== undefined) body.rep_id = patch.repId;
      const env = await httpRequestWithRefresh<CodeUpdateResultDto>(
        config,
        'PATCH',
        `${BASE}/codes/${encodeURIComponent(code)}`,
        body,
      );
      const result = unwrapResult(env, 'referralAdmin.updateCode');
      return {
        code: mapReferralCodeDto(result.code),
        affectsFutureOnly: result.affects_future_only === true,
      };
    },

    // ── 客戶歸屬(A8–A12)─────────────────────────────────────────────────────
    async listCustomers(opts) {
      const env = await httpRequestWithRefresh<ReferralCustomersDto>(
        config,
        'GET',
        `${BASE}/customers${qs({
          code: opts?.code,
          rep_id: opts?.repId,
          // BE 是 bool Query 預設 false:false 就不上 wire,true 才帶。
          unassigned: opts?.unassigned === true ? 'true' : undefined,
          q: opts?.q,
          limit: opts?.limit,
          cursor: opts?.cursor,
        })}`,
      );
      return mapReferralCustomersDto(unwrapResult(env, 'referralAdmin.listCustomers'));
    },

    async listBindings(groupId) {
      const env = await httpRequestWithRefresh<ReferralBindingHistoryDto>(
        config,
        'GET',
        `${BASE}/bindings${qs({ group_id: groupId })}`,
      );
      const result = unwrapResult(env, 'referralAdmin.listBindings');
      return (result.bindings ?? []).map(mapReferralBindingDto);
    },

    async createBinding(req) {
      const body: Record<string, unknown> = { group_id: req.groupId, code: req.code };
      putIf(body, 'effective_from', req.effectiveFrom);
      putIf(body, 'reason', req.reason);
      const env = await httpRequestWithRefresh<AdminBindResultDto>(
        config,
        'POST',
        `${BASE}/bindings`,
        body,
      );
      return mapAdminBindResultDto(unwrapResult(env, 'referralAdmin.createBinding'));
    },

    async voidBinding(bindingId, reason) {
      const env = await httpRequestWithRefresh<{ binding_id: string; status: string }>(
        config,
        'DELETE',
        `${BASE}/bindings/${encodeURIComponent(bindingId)}`,
        { reason },
      );
      unwrapResult(env, 'referralAdmin.voidBinding');
    },

    async bulkReassign(req) {
      const body: Record<string, unknown> = {
        from_rep_id: req.fromRepId,
        to_code: req.toCode,
        reason: req.reason,
      };
      putIf(body, 'effective_from', req.effectiveFrom);
      // dry-run 是 BE 預設(apply=false):不帶 apply 鍵 = dry-run;apply 時
      // expected_count 必帶(0 也是合法值,不能用 putIf 的空值判準)。
      if (req.apply === true) {
        body.apply = true;
        if (req.expectedCount !== undefined) body.expected_count = req.expectedCount;
      }
      const env = await httpRequestWithRefresh<BulkReassignResultDto>(
        config,
        'POST',
        `${BASE}/bindings/bulk-reassign`,
        body,
      );
      return mapBulkReassignResultDto(unwrapResult(env, 'referralAdmin.bulkReassign'));
    },

    // ── 月結(A13/A14/A22)────────────────────────────────────────────────────
    async listPeriods(limit) {
      const env = await httpRequestWithRefresh<{ periods: ReferralPeriodSummaryDto[] }>(
        config,
        'GET',
        `${BASE}/periods${qs({ limit })}`,
      );
      const result = unwrapResult(env, 'referralAdmin.listPeriods');
      return (result.periods ?? []).map(mapReferralPeriodSummaryDto);
    },

    async getPeriod(period) {
      const env = await httpRequestWithRefresh<ReferralPeriodSummaryDto>(
        config,
        'GET',
        `${BASE}/periods/${encodeURIComponent(period)}`,
      );
      return mapReferralPeriodSummaryDto(unwrapResult(env, 'referralAdmin.getPeriod'));
    },

    async recomputePeriod(period) {
      const env = await httpRequestWithRefresh<RecomputePeriodResultDto>(
        config,
        'POST',
        `${BASE}/periods/${encodeURIComponent(period)}/recompute`,
        {},
      );
      return mapRecomputePeriodResultDto(unwrapResult(env, 'referralAdmin.recomputePeriod'));
    },

    // ── 對帳單(A15–A21)──────────────────────────────────────────────────────
    async listStatements(opts) {
      const env = await httpRequestWithRefresh<{ statements: ReferralStatementDto[] }>(
        config,
        'GET',
        `${BASE}/statements${qs({ period: opts.period, rep_id: opts.repId, status: opts.status, limit: opts.limit })}`,
      );
      const result = unwrapResult(env, 'referralAdmin.listStatements');
      return (result.statements ?? []).map(mapReferralStatementDto);
    },

    async getStatement(period, repId) {
      const env = await httpRequestWithRefresh<ReferralStatementDetailDto>(
        config,
        'GET',
        `${BASE}/statements/${encodeURIComponent(period)}/${encodeURIComponent(repId)}`,
      );
      return mapReferralStatementDetailDto(unwrapResult(env, 'referralAdmin.getStatement'));
    },

    async lockStatement(period, repId, expectedTotalCommissionPoints) {
      const env = await httpRequestWithRefresh<{ status: string }>(
        config,
        'POST',
        `${BASE}/statements/${encodeURIComponent(period)}/${encodeURIComponent(repId)}/lock`,
        { expected_total_commission_points: expectedTotalCommissionPoints },
      );
      unwrapResult(env, 'referralAdmin.lockStatement');
    },

    async voidStatement(period, repId, reason) {
      const env = await httpRequestWithRefresh<{ revision: number }>(
        config,
        'POST',
        `${BASE}/statements/${encodeURIComponent(period)}/${encodeURIComponent(repId)}/void`,
        { reason },
      );
      const result = unwrapResult(env, 'referralAdmin.voidStatement');
      return { revision: result.revision };
    },

    async markPaid(period, repId) {
      const env = await httpRequestWithRefresh<{ status: string }>(
        config,
        'POST',
        `${BASE}/statements/${encodeURIComponent(period)}/${encodeURIComponent(repId)}/mark-paid`,
        {},
      );
      unwrapResult(env, 'referralAdmin.markPaid');
    },

    async addAdjustment(period, repId, req) {
      const body: Record<string, unknown> = {
        amount_points: req.amountPoints,
        reason: req.reason,
      };
      putIf(body, 'ref_period', req.refPeriod);
      const env = await httpRequestWithRefresh<{
        adjustment_points: number;
        payable_points: number;
      }>(
        config,
        'POST',
        `${BASE}/statements/${encodeURIComponent(period)}/${encodeURIComponent(repId)}/adjustments`,
        body,
      );
      const result = unwrapResult(env, 'referralAdmin.addAdjustment');
      return { adjustmentPoints: result.adjustment_points, payablePoints: result.payable_points };
    },

    async exportStatementsCsv(period) {
      const path = `${BASE}/statements/${encodeURIComponent(period)}/export`;
      const fetchImpl = config.fetchImpl ?? fetch;
      const resp = await fetchImpl(buildApiUrl(path, config.baseUrl), {
        method: 'GET',
        credentials: 'include',
      });
      if (!resp.ok) {
        // raw 端點的錯誤仍是 JSON envelope(FEATURE_DISABLED / 422 期別格式等)——
        // 解析得出就照 code 走 errorMap;解析不出(WAF/HTML)退 HTTP_ 狀態碼 slug。
        let error: ReturnType<typeof normalizeEnvelopeError> = null;
        try {
          const parsed: unknown = await resp.json();
          error =
            normalizeEnvelopeError((parsed as { error?: never })?.error) ??
            extractDetailObjectError(parsed);
        } catch {
          /* HTML / 空 body:走 fallback slug */
        }
        throw new ApiHttpError(
          resp.status,
          error ?? { code: `HTTP_${resp.status}`, message: resp.statusText || 'export failed' },
        );
      }
      const blob = await resp.blob();
      // Content-Disposition: attachment; filename="referral-commission-2026-09-DRAFT.csv"
      const dispo = resp.headers.get('content-disposition') ?? '';
      const m = /filename="([^"]+)"/.exec(dispo);
      return { filename: m?.[1] ?? `referral-commission-${period}.csv`, blob };
    },

    // ── 稽核(A23)────────────────────────────────────────────────────────────
    async listAuditLogs(opts) {
      const env = await httpRequestWithRefresh<ReferralAuditLogsDto>(
        config,
        'GET',
        `${BASE}/audit-logs${qs({
          target_kind: opts?.targetKind,
          target_id: opts?.targetId,
          action: opts?.action,
          actor: opts?.actor,
          limit: opts?.limit,
          cursor: opts?.cursor,
        })}`,
      );
      return mapReferralAuditLogsDto(unwrapResult(env, 'referralAdmin.listAuditLogs'));
    },
  };
}
